import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';

import {
  ApiError,
  createDataSource,
  createPage,
  saveDocument,
  searchPages,
  uploadFile,
} from '../api';
import type { CommentThread, PageSummary, WorkspaceMember } from '../api';
import type { MessageKey } from '../i18n';
import { t } from '../i18n';
import { MentionList, ThreadCard } from './Comments';
import {
  BlockRowAttributes,
  ChildPage,
  DatabaseBlock,
  blockSelectionPluginKey,
  Callout,
  CommentMark,
  Mention,
  PageLink,
  Toggle,
} from './extensions';
import type { DatabaseContext } from './extensions';
import { Glyph, ICON } from './icons';
import { documentToMarkdown, markdownToDocument } from './markdown';
import { BlockActionMenu } from './BlockActionMenu';
import { EditorFormatControls } from './EditorFormatControls';
import { trackBlockDrag } from './block-drag';
import { InsertBar } from './InsertBar';
import { useIsMobile } from './useIsMobile';
import { SLASH_MENU_ID, SlashMenu, slashOptionId } from './SlashMenu';
import type { SlashCaret } from './SlashMenu';
import { MENTION_MENU_ID, MentionPicker, mentionOptionId } from './MentionPicker';
import { filterMembers, mentionQueryAt } from './mention';
import { TurnIntoMenu } from './SelectionActionMenus';
import { useToast } from './Toast';
import { createSimpleTable, SIMPLE_TABLE_EXTENSIONS } from './SimpleTable';
import { Media } from './Media';
import type { MediaKind, MediaOptions } from './Media';
import { Backlinks } from './Backlinks';
import { TableOfContents } from './TableOfContents';

type BlockId =
  | 'text'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'bullet'
  | 'numbered'
  | 'todo'
  | 'toggle'
  | 'th1'
  | 'th2'
  | 'th3'
  | 'th4'
  | 'quote'
  | 'callout'
  | 'divider'
  | 'code'
  | 'database'
  | 'table'
  | 'toc'
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'file'
  | 'childpage';

interface BlockDef {
  id: BlockId;
  glyph: string;
  label: MessageKey;
  desc: MessageKey;
  /** The group in the slash menu. */
  group?: 'basic' | 'media';
}

const BLOCKS: BlockDef[] = [
  { id: 'text', glyph: 'Aa', label: 'editor.text', desc: 'editor.text.desc' },
  { id: 'h1', glyph: 'H1', label: 'editor.h1', desc: 'editor.h1.desc' },
  { id: 'h2', glyph: 'H2', label: 'editor.h2', desc: 'editor.h2.desc' },
  { id: 'h3', glyph: 'H3', label: 'editor.h3', desc: 'editor.h3.desc' },
  { id: 'h4', glyph: 'H4', label: 'editor.h4', desc: 'editor.h4.desc' },
  { id: 'bullet', glyph: '•', label: 'editor.bullet', desc: 'editor.bullet.desc' },
  { id: 'numbered', glyph: '1.', label: 'editor.numbered', desc: 'editor.numbered.desc' },
  { id: 'todo', glyph: '[ ]', label: 'editor.todo', desc: 'editor.todo.desc' },
  { id: 'toggle', glyph: '▸', label: 'editor.toggle', desc: 'editor.toggle.desc' },
  { id: 'th1', glyph: '▸H', label: 'editor.th1', desc: 'editor.th1.desc' },
  { id: 'th2', glyph: '▸H', label: 'editor.th2', desc: 'editor.th2.desc' },
  { id: 'th3', glyph: '▸H', label: 'editor.th3', desc: 'editor.th3.desc' },
  { id: 'th4', glyph: '▸H', label: 'editor.th4', desc: 'editor.th4.desc' },
  { id: 'quote', glyph: '“', label: 'editor.quote', desc: 'editor.quote.desc' },
  { id: 'callout', glyph: '!', label: 'editor.callout', desc: 'editor.callout.desc' },
  { id: 'divider', glyph: '—', label: 'editor.divider', desc: 'editor.divider.desc' },
  { id: 'code', glyph: '{ }', label: 'editor.codeBlock', desc: 'editor.codeBlock.desc' },
  { id: 'toc', glyph: '≡', label: 'editor.tableOfContents', desc: 'editor.tableOfContents.desc' },
  { id: 'table', glyph: '▦', label: 'editor.table', desc: 'editor.table.desc' },
  { id: 'database', glyph: '▦', label: 'editor.database', desc: 'editor.database.desc' },
  { id: 'image', glyph: '🖼', label: 'media.image', desc: 'media.image.desc', group: 'media' },
  { id: 'video', glyph: '🎬', label: 'media.video', desc: 'media.video.desc', group: 'media' },
  { id: 'audio', glyph: '🎧', label: 'media.audio', desc: 'media.audio.desc', group: 'media' },
  { id: 'pdf', glyph: '📄', label: 'media.pdf', desc: 'media.pdf.desc', group: 'media' },
  { id: 'file', glyph: '📎', label: 'media.file', desc: 'media.file.desc', group: 'media' },
  { id: 'childpage', glyph: '↳', label: 'editor.childPage', desc: 'editor.childPage.desc' },
];

/** The blocks that need a file, so they cannot be reached by turning into one. */
const MEDIA_BLOCKS: BlockId[] = ['image', 'video', 'audio', 'pdf', 'file'];

/** What the file picker accepts per kind. */
const MEDIA_ACCEPT: Partial<Record<BlockId, string>> = {
  image: 'image/png,image/jpeg,image/gif,image/webp',
  video: 'video/mp4,video/webm',
  audio: 'audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/flac',
  pdf: 'application/pdf',
  file: '*/*',
};

/** A Markdown file is imported as blocks, not uploaded. */
export function isMarkdownFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.md') || name.endsWith('.markdown') || file.type === 'text/markdown';
}

/**
 * The block boundary nearest the drop, so a file never lands inside a line.
 * A drop in the page margin above the text lands first, below it lands last.
 */
function dropPosition(view: EditorView, event: DragEvent): number {
  const hit = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (!hit) {
    const rect = view.dom.getBoundingClientRect();
    return event.clientY < rect.top ? 0 : view.state.doc.content.size;
  }
  const $pos = view.state.doc.resolve(hit.pos);
  return $pos.depth > 0 ? $pos.after($pos.depth) : hit.pos;
}

// A database is created through the API, so a line cannot be turned into one.
const TURN_BLOCKS = BLOCKS.filter(
  (block) =>
    block.id !== 'divider' &&
    block.id !== 'database' &&
    block.id !== 'table' &&
    block.id !== 'toc' &&
    !MEDIA_BLOCKS.includes(block.id),
);

// Line icons for Turn into, from the prototype's TURN_ICON set (e1.js).
const HEADING_ICON = 'M5 5v14 M13 5v14 M5 12h8';
const TURN_ICON: Partial<Record<BlockId, { d: string; sub: string }>> = {
  text: { d: 'M5 6V4h14v2 M12 4v16 M9 20h6', sub: '' },
  h1: { d: HEADING_ICON, sub: '1' },
  h2: { d: HEADING_ICON, sub: '2' },
  h3: { d: HEADING_ICON, sub: '3' },
  h4: { d: HEADING_ICON, sub: '4' },
  bullet: { d: 'M9 6h12 M9 12h12 M9 18h12 M4 6h.01 M4 12h.01 M4 18h.01', sub: '' },
  numbered: { d: 'M10 6h11 M10 12h11 M10 18h11 M4 4.5l1.5-1V9 M3.5 14h3l-3 4h3', sub: '' },
  todo: { d: 'M3.5 6.5l1.5 1.5 3-3 M4 15h4v4H4z M12 7h9 M12 17h9', sub: '' },
  toggle: { d: 'M3 5l4 2.5L3 10z M11 7.5h10 M11 14h10 M11 19h10', sub: '' },
  th1: { d: `${HEADING_ICON} M1 4l3 2-3 2z`, sub: '1' },
  th2: { d: `${HEADING_ICON} M1 4l3 2-3 2z`, sub: '2' },
  th3: { d: `${HEADING_ICON} M1 4l3 2-3 2z`, sub: '3' },
  th4: { d: `${HEADING_ICON} M1 4l3 2-3 2z`, sub: '4' },
  quote: {
    d: 'M8 8c-2.5.5-4 2-4 4.5V17h5v-4.5H6 M18 8c-2.5.5-4 2-4 4.5V17h5v-4.5h-3',
    sub: '',
  },
  callout: {
    d: 'M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z M9 9h6 M12 9v7',
    sub: '',
  },
  code: { d: 'M16 18l6-6-6-6 M8 6l-6 6 6 6', sub: '' },
};

function BlockIcon({ id }: { id: BlockId }): React.ReactElement | null {
  const icon = TURN_ICON[id];
  if (!icon) return null;
  return (
    <span className="blk-icon" aria-hidden="true">
      <Glyph d={icon.d} size={18} />
      {icon.sub ? <span className="blk-icon__sub">{icon.sub}</span> : null}
    </span>
  );
}

const PLACEHOLDERS: Record<string, MessageKey> = {
  paragraph: 'editor.ph.text',
  h1: 'editor.h1',
  h2: 'editor.h2',
  h3: 'editor.h3',
  h4: 'editor.h4',
};

export function blockContent(id: BlockId, inline?: JSONContent[]): JSONContent {
  const content = inline && inline.length > 0 ? inline : undefined;
  const paragraph: JSONContent = { type: 'paragraph', content };
  switch (id) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
      return { type: 'heading', attrs: { level: Number(id.slice(1)) }, content };
    case 'toc':
      return { type: 'tableOfContents' };
    case 'table':
      return createSimpleTable();
    case 'bullet':
      return { type: 'bulletList', content: [{ type: 'listItem', content: [paragraph] }] };
    case 'numbered':
      return { type: 'orderedList', content: [{ type: 'listItem', content: [paragraph] }] };
    case 'todo':
      return {
        type: 'taskList',
        content: [{ type: 'taskItem', attrs: { checked: false }, content: [paragraph] }],
      };
    case 'toggle':
      return { type: 'toggle', attrs: { open: true, level: 0 }, content: [paragraph] };
    case 'th1':
    case 'th2':
    case 'th3':
    case 'th4': {
      const level = Number(id.slice(2));
      return {
        type: 'toggle',
        attrs: { open: true, level },
        content: [{ type: 'heading', attrs: { level }, content }],
      };
    }
    case 'quote':
      return { type: 'blockquote', content: [paragraph] };
    case 'callout':
      return { type: 'callout', attrs: { icon: '💡' }, content: [paragraph] };
    case 'divider':
      return { type: 'horizontalRule' };
    case 'code':
      return {
        type: 'codeBlock',
        content: content
          ? [{ type: 'text', text: content.map((node) => node.text ?? '').join('') }]
          : undefined,
      };
    default:
      return paragraph;
  }
}

function collectInline(node: JSONContent, out: JSONContent[]): void {
  if (node.type === 'text' && node.text) {
    out.push({ type: 'text', text: node.text, marks: node.marks });
    return;
  }
  for (const child of node.content ?? []) collectInline(child, out);
}

/**
 * Depth of the block Turn into should replace. A line inside a callout stays
 * inside it: the callout's child is converted, not the callout itself.
 * `ancestors` lists node types from depth 1 through the parent of the cursor.
 */
export function conversionDepth(ancestors: readonly string[]): number | null {
  if (ancestors.length === 0) return null;
  for (let index = ancestors.length - 1; index >= 0; index -= 1) {
    if (ancestors[index] === 'callout' && index + 1 < ancestors.length) return index + 2;
  }
  return 1;
}

function blockAtSelection(
  editor: Editor,
): { pos: number; node: JSONContent; kind: BlockId } | null {
  const { $from } = editor.state.selection;
  const ancestors: string[] = [];
  for (let depth = 1; depth <= $from.depth; depth += 1) ancestors.push($from.node(depth).type.name);
  const depth = conversionDepth(ancestors);
  if (depth === null || depth > $from.depth) return null;
  const node = $from.node(depth).toJSON() as JSONContent;
  return { pos: $from.before(depth), node, kind: kindOf(node) };
}

function blockAtPos(
  editor: Editor,
  pos: number,
): { pos: number; node: JSONContent; kind: BlockId } | null {
  const node = editor.state.doc.nodeAt(pos);
  if (!node) return null;
  const json = node.toJSON() as JSONContent;
  return { pos, node: json, kind: kindOf(json) };
}

function kindOf(node: JSONContent): BlockId {
  switch (node.type) {
    case 'tableOfContents':
      return 'toc';
    case 'table':
      return 'table';
    case 'heading':
      return `h${node.attrs?.['level'] ?? 1}` as BlockId;
    case 'bulletList':
      return 'bullet';
    case 'orderedList':
      return 'numbered';
    case 'taskList':
      return 'todo';
    case 'toggle': {
      const level = node.attrs?.['level'];
      return typeof level === 'number' && level >= 1 && level <= 4
        ? (`th${level}` as BlockId)
        : 'toggle';
    }
    case 'blockquote':
      return 'quote';
    case 'callout':
      return 'callout';
    case 'horizontalRule':
      return 'divider';
    case 'codeBlock':
      return 'code';
    default:
      return 'text';
  }
}

function textOffset(block: JSONContent): number {
  switch (block.type) {
    case 'table':
      return 4;
    case 'bulletList':
    case 'orderedList':
    case 'taskList':
      return 3;
    case 'blockquote':
    case 'toggle':
    case 'callout':
      return 2;
    default:
      return 1;
  }
}

/** Grip sits on the callout; a heading conversion restyles the line under the cursor. */
function calloutLinePos(editor: Editor, calloutPos: number): number {
  const { $from } = editor.state.selection;
  for (let depth = 1; depth < $from.depth; depth += 1) {
    if ($from.node(depth).type.name === 'callout' && $from.before(depth) === calloutPos) {
      return $from.before(depth + 1);
    }
  }
  return calloutPos + 1;
}

function replaceBlock(editor: Editor, id: BlockId, preserve: boolean, at?: number): void {
  const top = at === undefined ? blockAtSelection(editor) : blockAtPos(editor, at);
  if (!top) return;
  // Toggle <-> toggle heading only changes the level and the header block;
  // the kid lines stay untouched (prototype convert()).
  const targetLevel = id === 'toggle' ? 0 : id.startsWith('th') ? Number(id.slice(2)) : null;
  if (top.node.type === 'toggle' && targetLevel !== null) {
    const level = targetLevel;
    editor
      .chain()
      .focus()
      .command(({ tr, state }) => {
        const node = state.doc.nodeAt(top.pos);
        if (!node) return false;
        tr.setNodeMarkup(top.pos, undefined, { ...node.attrs, level, open: true });
        const first = node.firstChild;
        if (first) {
          const type =
            level === 0 ? state.schema.nodes['paragraph'] : state.schema.nodes['heading'];
          if (type) tr.setNodeMarkup(top.pos + 1, type, level === 0 ? {} : { level });
        }
        return true;
      })
      .setTextSelection(top.pos + 2)
      .run();
    return;
  }
  const row = typeof top.node.attrs?.['row'] === 'string' ? top.node.attrs['row'] : null;
  const cw = typeof top.node.attrs?.['cw'] === 'number' ? top.node.attrs['cw'] : null;
  const color = typeof top.node.attrs?.['color'] === 'string' ? top.node.attrs['color'] : null;
  const bg = typeof top.node.attrs?.['bg'] === 'string' ? top.node.attrs['bg'] : null;
  const preservedAttrs: Record<string, unknown> = {};
  if (row) {
    preservedAttrs['row'] = row;
    preservedAttrs['cw'] = cw ?? 1;
  }
  if (color) preservedAttrs['color'] = color;
  if (bg) preservedAttrs['bg'] = bg;
  const inline: JSONContent[] = [];
  if (preserve) collectInline(top.node, inline);
  const rawBlock = blockContent(id, preserve ? inline : undefined);
  const block: JSONContent = {
    ...rawBlock,
    attrs: { ...rawBlock.attrs, ...preservedAttrs },
  };
  const size = editor.state.doc.nodeAt(top.pos)?.nodeSize ?? 0;
  const chain = editor
    .chain()
    .focus()
    .insertContentAt({ from: top.pos, to: top.pos + size }, block);
  if (id !== 'divider' && id !== 'toc') chain.setTextSelection(top.pos + textOffset(block));
  chain.run();
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

interface GutterState {
  pos: number;
  top: number;
  left: number;
  kind: BlockId;
}

interface DropIndicator {
  pos: number;
  side: 'left' | 'right' | null;
  after?: boolean;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ColHandle {
  pos: number;
  top: number;
  left: number;
  height: number;
  hovered: boolean;
}

interface MarqueeBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

const COLUMN_GAP = 52;
const COLUMN_SNAPS = [1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4] as const;

const BLOCK_TEXT_COLORS: ReadonlyArray<{ id: string; label: MessageKey }> = [
  { id: '', label: 'editor.colorDefault' },
  { id: 'gray', label: 'editor.colorGray' },
  { id: 'brown', label: 'editor.colorBrown' },
  { id: 'orange', label: 'editor.colorOrange' },
  { id: 'yellow', label: 'editor.colorYellow' },
  { id: 'green', label: 'editor.colorGreen' },
  { id: 'blue', label: 'editor.colorBlue' },
  { id: 'purple', label: 'editor.colorPurple' },
  { id: 'pink', label: 'editor.colorPink' },
  { id: 'red', label: 'editor.colorRed' },
];

const BLOCK_BG_COLORS: ReadonlyArray<{ id: string; label: MessageKey }> = [
  { id: '', label: 'editor.colorDefault' },
  { id: 'gray', label: 'editor.colorGray' },
  { id: 'yellow', label: 'editor.colorYellow' },
  { id: 'green', label: 'editor.colorGreen' },
  { id: 'blue', label: 'editor.colorBlue' },
  { id: 'pink', label: 'editor.colorPink' },
  { id: 'red', label: 'editor.colorRed' },
];

function snapColumnFraction(fraction: number): number {
  for (const target of COLUMN_SNAPS) {
    if (Math.abs(fraction - target) < 0.025) return target;
  }
  return fraction;
}

function tidyBlockRows(nodes: JSONContent[]): JSONContent[] {
  const groups: Record<string, JSONContent[]> = {};
  for (const node of nodes) {
    const row = typeof node.attrs?.['row'] === 'string' ? node.attrs['row'] : null;
    if (row) {
      groups[row] = groups[row] ?? [];
      groups[row].push(node);
    }
  }
  const emittedRows = new Set<string>();
  const coalesced: JSONContent[] = [];
  for (const node of nodes) {
    const row = typeof node.attrs?.['row'] === 'string' ? node.attrs['row'] : null;
    if (!row) {
      coalesced.push(node);
      continue;
    }
    if (emittedRows.has(row)) continue;
    emittedRows.add(row);
    const members = groups[row] ?? [node];
    if (members.length < 2) {
      for (const member of members) {
        coalesced.push({
          ...member,
          attrs: { ...member.attrs, row: null, cw: null },
        });
      }
    } else {
      coalesced.push(...members);
    }
  }
  return coalesced;
}

function convertBlockJson(node: JSONContent, targetId: BlockId): JSONContent {
  if (node.type === 'horizontalRule') return node;
  const row = typeof node.attrs?.['row'] === 'string' ? node.attrs['row'] : null;
  const cw = typeof node.attrs?.['cw'] === 'number' ? node.attrs['cw'] : null;
  const color = typeof node.attrs?.['color'] === 'string' ? node.attrs['color'] : null;
  const bg = typeof node.attrs?.['bg'] === 'string' ? node.attrs['bg'] : null;
  const preservedAttrs: Record<string, unknown> = {};
  if (row) {
    preservedAttrs['row'] = row;
    preservedAttrs['cw'] = cw ?? 1;
  }
  if (color) preservedAttrs['color'] = color;
  if (bg) preservedAttrs['bg'] = bg;
  const targetLevel =
    targetId === 'toggle' ? 0 : targetId.startsWith('th') ? Number(targetId.slice(2)) : null;
  if (node.type === 'toggle' && targetLevel !== null) {
    const kids = (node.content ?? []).slice();
    const first = kids[0] ?? { type: 'paragraph' };
    kids[0] =
      targetLevel === 0
        ? { type: 'paragraph', content: first.content }
        : { type: 'heading', attrs: { level: targetLevel }, content: first.content };
    return {
      ...node,
      attrs: { ...node.attrs, level: targetLevel, open: true, ...preservedAttrs },
      content: kids,
    };
  }
  const inline: JSONContent[] = [];
  collectInline(node, inline);
  const converted = blockContent(targetId, inline);
  return {
    ...converted,
    attrs: { ...converted.attrs, ...preservedAttrs },
  };
}

// Prototype gutStyle margins are relative to the row's content box; ours is
// the border box, so each value carries the 4px row padding. The 30px-tall
// buttons then centre on the first text line.
const GUTTER_OFFSET: Partial<Record<BlockId, number>> = {
  h1: 22,
  h2: 15,
  h3: 9,
  h4: 5,
  toggle: 1,
  th1: 22,
  th2: 15,
  th3: 9,
  th4: 5,
  callout: 18,
};

/** Ranges whose comment mark points at a thread that no longer exists. */
export function orphanCommentRanges(
  doc: ProseMirrorNode,
  liveThreadIds: ReadonlySet<string>,
): Array<{ from: number; to: number; threadId: string }> {
  const ranges: Array<{ from: number; to: number; threadId: string }> = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    for (const mark of node.marks) {
      if (mark.type.name !== 'comment') continue;
      const threadId = mark.attrs['threadId'];
      if (typeof threadId === 'string' && !liveThreadIds.has(threadId)) {
        ranges.push({ from: pos, to: pos + node.nodeSize, threadId });
      }
    }
  });
  return ranges;
}

export interface PageEditorComments {
  /** False until the thread list for this page has loaded. */
  loaded: boolean;
  threads: CommentThread[];
  members: WorkspaceMember[];
  currentUserId: string;
  showResolved: boolean;
  authorFilter: string;
  hidden: boolean;
  pageComposeOpen: boolean;
  onClosePageCompose: () => void;
  onShowComments: () => void;
  onCreateThread: (quote: string | null, text: string) => Promise<CommentThread>;
  onReply: (threadId: string, text: string) => Promise<void>;
  onEdit: (commentId: string, text: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onSetResolved: (threadId: string, resolved: boolean) => Promise<void>;
}

export interface PageEditorActions {
  undo: () => void;
  redo: () => void;
  toMarkdown: () => string;
  replaceFromMarkdown: (markdown: string) => boolean;
}

interface ComposeState {
  quote: string;
  from: number;
  to: number;
  top: number;
  left: number;
}

/** A mention being typed, with the range it will replace. */
interface MentionState {
  query: string;
  from: number;
  to: number;
  caret: SlashCaret;
}

/** Replace a typed `@name` with a mention of that person. */
function insertMention(current: Editor, from: number, to: number, member: WorkspaceMember): void {
  current
    .chain()
    .focus()
    .deleteRange({ from, to })
    .insertContentAt(from, [
      { type: 'mention', attrs: { userId: member.id, name: member.name } },
      { type: 'text', text: ' ' },
    ])
    .run();
}

/**
 * The mention being typed at the cursor, if any.
 *
 * The text of the block is read with one placeholder character per inline
 * node, so a character in that text is one position in the document and the
 * range we replace is exact.
 */
function currentMention(current: Editor): MentionState | null {
  const $from = current.state.selection.$from;
  const parent = $from.parent;
  if (!parent.isTextblock || parent.type.name === 'codeBlock') return null;
  const before = parent.textBetween(0, $from.parentOffset, undefined, '￼');
  const found = mentionQueryAt(before, before.length, $from.start());
  if (!found) return null;
  const coords = current.view.coordsAtPos(current.state.selection.from);
  return {
    ...found,
    caret: { top: coords.top, bottom: coords.bottom, left: coords.left },
  };
}

export function PageEditor({
  pageId,
  workspaceId,
  version,
  content,
  comments,
  locked,
  actionsRef,
  onOpenRow,
}: {
  pageId: string;
  workspaceId: string;
  version: number;
  content: JSONContent;
  comments: PageEditorComments;
  locked: boolean;
  actionsRef: React.RefObject<PageEditorActions | null>;
  onOpenRow: (rowId: string) => void;
}): React.ReactElement {
  const toast = useToast();
  const versionRef = useRef(version);
  const lockedRef = useRef(locked);
  const wrapRef = useRef<HTMLDivElement>(null);
  const saveTimer = useRef<number | null>(null);
  const dragPosRef = useRef<number | null>(null);
  const dragPositionsRef = useRef<number[] | null>(null);
  const dropIndicatorRef = useRef<DropIndicator | null>(null);
  const didGripDragRef = useRef(false);
  const [status, setStatus] = useState<SaveState>('idle');
  const [editorFocused, setEditorFocused] = useState(false);
  const stopDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => stopDragRef.current?.(), []);
  const isMobile = useIsMobile();
  const [mobileMoving, setMobileMoving] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [slashCaret, setSlashCaret] = useState<SlashCaret | null>(null);
  const [mention, setMention] = useState<MentionState | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [gutter, setGutter] = useState<GutterState | null>(null);
  const [blockMenu, setBlockMenu] = useState<GutterState | null>(null);
  const [blockMenuQuery, setBlockMenuQuery] = useState('');
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const [selectedPositions, setSelectedPositions] = useState<number[]>([]);
  const [gripSelectedPos, setGripSelectedPos] = useState<number | null>(null);
  const [mselTurnOpen, setMselTurnOpen] = useState(false);
  const [marquee, setMarquee] = useState<MarqueeBox | null>(null);
  const [colHandles, setColHandles] = useState<ColHandle[]>([]);
  const [colDraggingPos, setColDraggingPos] = useState<number | null>(null);
  // The bar hides itself when focus leaves it, so its menu has to live inside it.
  const selbarRef = useRef<HTMLDivElement | null>(null);
  const [selSub, setSelSub] = useState<'turn' | 'link' | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [pageHits, setPageHits] = useState<PageSummary[]>([]);
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [composeDraft, setComposeDraft] = useState('');
  const [composeFailed, setComposeFailed] = useState(false);
  const [pageDraft, setPageDraft] = useState('');
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [threadTops, setThreadTops] = useState<Record<string, number>>({});
  const [, setSelTick] = useState(0);
  const slashRef = useRef({ open: false, index: 0, ids: [] as BlockId[] });
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const mediaTargetRef = useRef<{ pos: number; size: number } | null>(null);
  const mediaPicker = useRef<MediaOptions['picker']['current']>(null);
  const mentionRef = useRef({
    open: false,
    index: 0,
    ids: [] as string[],
    members: [] as WorkspaceMember[],
    from: 0,
    to: 0,
  });
  const databaseContext = useRef<DatabaseContext | null>(null);
  databaseContext.current = { pageId, workspaceId, openRow: onOpenRow };

  /** Create a data source, then drop the block that points at it in the doc. */
  /** Open the file picker for a media block, at the line the slash opened. */
  const pickMedia = (id: BlockId): void => {
    const top = editor ? blockAtSelection(editor) : null;
    if (!editor || !top) return;
    mediaTargetRef.current = {
      pos: top.pos,
      size: editor.state.doc.nodeAt(top.pos)?.nodeSize ?? 0,
    };
    const input = mediaInputRef.current;
    if (input) {
      input.accept = MEDIA_ACCEPT[id] ?? '*/*';
      input.click();
    }
    setQuery(null);
  };

  /** An emptied media block asks for a new file; it is replaced in place. */
  mediaPicker.current = (kind: MediaKind, pos: number): void => {
    const node = editor?.state.doc.nodeAt(pos);
    const input = mediaInputRef.current;
    if (!node || !input) return;
    mediaTargetRef.current = { pos, size: node.nodeSize };
    input.accept = MEDIA_ACCEPT[kind] ?? '*/*';
    input.click();
  };

  /** Upload the chosen file and put it where the slash line was. */
  const attachMedia = (file: File): void => {
    const target = mediaTargetRef.current;
    mediaTargetRef.current = null;
    if (!editor || !target) return;
    void uploadFile(pageId, file)
      .then((stored) => {
        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: target.pos, to: target.pos + target.size },
            {
              type: 'media',
              attrs: {
                fileId: stored.id,
                kind: stored.kind,
                filename: stored.filename,
                contentType: stored.contentType,
                size: stored.size,
              },
            },
          )
          .run();
      })
      .catch(() => toast(t('media.uploadFailed')));
  };

  /** Dropped files land where the cursor is: images and PDFs as media, Markdown as blocks. */
  const insertDroppedFiles = (view: EditorView, files: File[], at: number): void => {
    void (async () => {
      let pos = at;
      for (const file of files) {
        try {
          if (isMarkdownFile(file)) {
            const doc = markdownToDocument(await file.text());
            const nodes = (doc.content ?? []).map((node) => view.state.schema.nodeFromJSON(node));
            if (nodes.length === 0) continue;
            view.dispatch(view.state.tr.insert(pos, nodes));
            pos += nodes.reduce((sum, node) => sum + node.nodeSize, 0);
          } else {
            const stored = await uploadFile(pageId, file);
            const node = view.state.schema.nodes['media']?.create({
              fileId: stored.id,
              kind: stored.kind,
              filename: stored.filename,
              contentType: stored.contentType,
              size: stored.size,
            });
            if (!node) continue;
            view.dispatch(view.state.tr.insert(pos, node));
            pos += node.nodeSize;
          }
        } catch {
          toast(t('media.uploadFailed'));
        }
      }
      view.focus();
    })();
  };

  const insertDatabase = (current: Editor): void => {
    void createDataSource({ pageId })
      .then((created) => {
        const top = blockAtSelection(current);
        const at = top ? top.pos : current.state.selection.from;
        const block: JSONContent = { type: 'database', attrs: { dataSourceId: created.id } };
        current
          .chain()
          .focus()
          .insertContentAt(at, [block, { type: 'paragraph' }])
          .setTextSelection(at + 1)
          .run();
      })
      .catch(() => toast(t('database.addRowFailed')));
  };
  dropIndicatorRef.current = dropIndicator;

  const items = useMemo(() => {
    const needle = (query ?? '').trim().toLowerCase();
    return BLOCKS.filter((block) => {
      if (!needle) return true;
      return t(block.label).toLowerCase().includes(needle) || block.id.includes(needle);
    });
  }, [query]);

  slashRef.current = { open: query !== null, index, ids: items.map((item) => item.id) };
  const activeSlashId = query !== null ? items[index]?.id : undefined;

  const mentionMembers = useMemo(
    () => (mention ? filterMembers(comments.members, mention.query) : []),
    [mention, comments.members],
  );
  mentionRef.current = {
    open: mention !== null && mentionMembers.length > 0,
    index: Math.min(mentionIndex, Math.max(mentionMembers.length - 1, 0)),
    ids: mentionMembers.map((member) => member.id),
    members: mentionMembers,
    from: mention?.from ?? 0,
    to: mention?.to ?? 0,
  };
  const activeMentionId = mentionRef.current.open
    ? mentionRef.current.ids[mentionRef.current.index]
    : undefined;

  const slashKeys = useMemo(
    () =>
      Extension.create({
        name: 'slashKeys',
        priority: 1000,
        addKeyboardShortcuts() {
          const pick = (current: Editor): boolean => {
            const slash = slashRef.current;
            const id = slash.ids[slash.index];
            if (!id) return false;
            if (id === 'database') insertDatabase(current);
            else if (id === 'childpage') createChildPage(current);
            else if (MEDIA_BLOCKS.includes(id)) pickMedia(id);
            else replaceBlock(current, id, false);
            setQuery(null);
            return true;
          };
          /** Replace the typed `@name` with a mention of that person. */
          const pickMention = (current: Editor): boolean => {
            const state = mentionRef.current;
            const member = state.members[state.index];
            if (!state.open || !member) return false;
            insertMention(current, state.from, state.to, member);
            setMention(null);
            return true;
          };
          return {
            ArrowDown: () => {
              const mention = mentionRef.current;
              if (mention.open && mention.ids.length > 0) {
                setMentionIndex((value) => (value + 1) % mention.ids.length);
                return true;
              }
              const slash = slashRef.current;
              if (!slash.open || slash.ids.length === 0) return false;
              setIndex((value) => (value + 1) % slash.ids.length);
              return true;
            },
            ArrowUp: () => {
              const mention = mentionRef.current;
              if (mention.open && mention.ids.length > 0) {
                setMentionIndex((value) => (value - 1 + mention.ids.length) % mention.ids.length);
                return true;
              }
              const slash = slashRef.current;
              if (!slash.open || slash.ids.length === 0) return false;
              setIndex((value) => (value - 1 + slash.ids.length) % slash.ids.length);
              return true;
            },
            Enter: ({ editor: current }) => {
              if (mentionRef.current.open) return pickMention(current);
              return slashRef.current.open ? pick(current) : false;
            },
            Tab: ({ editor: current }) => {
              if (mentionRef.current.open) return pickMention(current);
              return slashRef.current.open ? pick(current) : false;
            },
            Escape: () => {
              if (mentionRef.current.open) {
                setMention(null);
                return true;
              }
              if (!slashRef.current.open) return false;
              setQuery(null);
              return true;
            },
          };
        },
      }),
    [],
  );

  const editor = useEditor(
    {
      extensions: [
        slashKeys,
        StarterKit.configure({
          heading: { levels: [1, 2, 3, 4] },
          link: { openOnClick: true },
        }),
        ...SIMPLE_TABLE_EXTENSIONS,
        TableOfContents,
        TaskList,
        TaskItem.configure({ nested: true }),
        Placeholder.configure({
          placeholder: ({ node }) => {
            const key =
              node.type.name === 'heading'
                ? PLACEHOLDERS[`h${node.attrs['level'] ?? 1}`]
                : PLACEHOLDERS[node.type.name];
            return key ? t(key) : '';
          },
        }),
        Toggle,
        Callout,
        Mention,
        Media.configure({ picker: mediaPicker }),
        PageLink,
        ChildPage,
        DatabaseBlock.configure({ context: databaseContext }),
        CommentMark,
        BlockRowAttributes,
      ],
      content,
      editorProps: {
        attributes: {
          'aria-label': t('page.placeholder'),
          class: 'page-editor__body',
        },
        // While a gutter drag is active the wrapper handles the drop; without
        // this ProseMirror inserts the drag payload as text. File drops are
        // handled by the DOM listener below (ProseMirror has no slice for them).
        handleDrop: () => dragPosRef.current !== null,
      },
      onFocus: () => setEditorFocused(true),
      onBlur: ({ event }) => {
        const next = event.relatedTarget;
        if (next instanceof Element && !next.closest('.insertbar, .insertbar__compose'))
          setEditorFocused(false);
      },
      onUpdate: ({ editor: current }) => {
        const parent = current.state.selection.$from.parent;
        const text = parent.textContent;
        if (
          parent.type.name === 'paragraph' &&
          !current.isActive('table') &&
          text.startsWith('/')
        ) {
          setQuery(text.slice(1));
          setIndex(0);
          const coords = current.view.coordsAtPos(current.state.selection.from);
          setSlashCaret({ top: coords.top, bottom: coords.bottom, left: coords.left });
        } else {
          setQuery(null);
        }

        const found = currentMention(current);
        if (found) {
          setMention(found);
          setMentionIndex(0);
        } else {
          setMention(null);
        }

        if (saveTimer.current) window.clearTimeout(saveTimer.current);
        const json = current.getJSON();
        saveTimer.current = window.setTimeout(() => {
          void persist(json);
        }, 500);
      },
      onCreate: () => setSelTick((value) => value + 1),
      onSelectionUpdate: () => setSelTick((value) => value + 1),
      // One editor per page mount (the parent keys by page id); without deps
      // TipTap recreates the editor on re-render and closures go stale.
    },
    [],
  );

  // A file drop works anywhere on the page canvas, not just on a selected
  // block: ProseMirror has no slice for files, so without this the browser
  // would navigate to the file. The block type follows the file itself.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const editorDom = editor.view.dom;
    const closest = editorDom.closest('.document') ?? editorDom.closest('.page');
    const canvas = closest instanceof HTMLElement ? closest : editorDom;
    const onDrop = (event: DragEvent): void => {
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0 || dragPosRef.current !== null) return;
      event.preventDefault();
      event.stopPropagation();
      insertDroppedFiles(editor.view, files, dropPosition(editor.view, event));
    };
    const onDragOver = (event: DragEvent): void => {
      if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
    };
    canvas.addEventListener('drop', onDrop);
    canvas.addEventListener('dragover', onDragOver);
    return () => {
      canvas.removeEventListener('drop', onDrop);
      canvas.removeEventListener('dragover', onDragOver);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const dom = editor.view.dom;
    if (activeSlashId === undefined) return;
    dom.setAttribute('aria-activedescendant', slashOptionId(activeSlashId));
    dom.setAttribute('aria-controls', SLASH_MENU_ID);
    return () => {
      dom.removeAttribute('aria-activedescendant');
      dom.removeAttribute('aria-controls');
    };
  }, [editor, activeSlashId]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const dom = editor.view.dom;
    if (activeMentionId === undefined) return;
    dom.setAttribute('aria-activedescendant', mentionOptionId(activeMentionId));
    dom.setAttribute('aria-controls', MENTION_MENU_ID);
    return () => {
      dom.removeAttribute('aria-activedescendant');
      dom.removeAttribute('aria-controls');
    };
  }, [editor, activeMentionId]);

  useEffect(() => {
    if (!isMobile || !editor) return;
    const updateFocus = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('.insertbar, .insertbar__compose, .slash'))
        return;
      const inside = editor.view.dom.contains(target);
      setEditorFocused(inside);
      if (!inside) {
        stopDragRef.current?.();
        setMobileMoving(false);
      }
    };
    document.addEventListener('pointerdown', updateFocus);
    return () => document.removeEventListener('pointerdown', updateFocus);
  }, [editor, isMobile]);

  useEffect(() => {
    if (!isMobile || locked) {
      stopDragRef.current?.();
      setMobileMoving(false);
    }
  }, [isMobile, locked]);

  useEffect(() => {
    versionRef.current = version;
  }, [version]);
  useEffect(() => {
    lockedRef.current = locked;
    editor?.setEditable(!locked);
  }, [editor, locked]);

  useEffect(() => {
    if (!editor) return;
    actionsRef.current = {
      undo: () => {
        editor.chain().focus().undo().run();
      },
      redo: () => {
        editor.chain().focus().redo().run();
      },
      toMarkdown: () => documentToMarkdown(editor.getJSON()),
      replaceFromMarkdown: (markdown) => {
        if (!editor.isEditable) return false;
        return editor.commands.setContent(markdownToDocument(markdown));
      },
    };
    return () => {
      actionsRef.current = null;
    };
  }, [actionsRef, editor]);

  async function persist(next: JSONContent): Promise<void> {
    if (lockedRef.current) return;
    setStatus('saving');
    try {
      const saved = await saveDocument(pageId, versionRef.current, next);
      versionRef.current = saved.version;
      setStatus('saved');
    } catch (error) {
      setStatus(error instanceof ApiError && error.status === 409 ? 'conflict' : 'error');
    }
  }

  const visibleThreads = comments.threads.filter(
    (thread) =>
      (!thread.resolved || comments.showResolved) &&
      (comments.authorFilter === 'all' ||
        thread.messages.some((message) => message.authorName === comments.authorFilter)),
  );
  const anchoredThreads = visibleThreads.filter((thread) => thread.id in threadTops);
  const pageThreads = visibleThreads.filter((thread) => !(thread.id in threadTops));

  useEffect(() => {
    if (!editor) return;
    const prev = blockSelectionPluginKey.getState(editor.state) ?? [];
    if (
      prev.length !== selectedPositions.length ||
      prev.some((pos, i) => pos !== selectedPositions[i])
    ) {
      editor.view.dispatch(
        editor.state.tr
          .setMeta(blockSelectionPluginKey, selectedPositions)
          .setMeta('addToHistory', false),
      );
    }
  }, [editor, selectedPositions]);

  // The yellow highlight is the comment mark. Drop it once its thread is gone,
  // after the list has loaded so a slow fetch cannot wipe every highlight.
  useEffect(() => {
    if (!editor || !comments.loaded) return;
    const live = new Set(comments.threads.map((thread) => thread.id));
    const ranges = orphanCommentRanges(editor.state.doc, live);
    if (openThreadId && !live.has(openThreadId)) setOpenThreadId(null);
    if (ranges.length === 0) return;
    const markType = editor.state.schema.marks['comment'];
    if (!markType) return;
    let tr = editor.state.tr;
    for (const range of ranges) {
      tr = tr.removeMark(range.from, range.to, markType.create({ threadId: range.threadId }));
    }
    editor.view.dispatch(tr.setMeta('addToHistory', false));
  }, [editor, comments.loaded, comments.threads, openThreadId]);

  // Anchor each open thread card to the top of the block that holds its mark,
  // and compute column resize handles. Recomputed after every render.
  useEffect(() => {
    if (!editor || !wrapRef.current) return;
    const wrapRect = wrapRef.current.getBoundingClientRect();
    const next: Record<string, number> = {};
    for (const thread of visibleThreads) {
      if (!thread.quote) continue;
      const mark = editor.view.dom.querySelector(`[data-comment-id="${thread.id}"]`);
      if (!mark) continue;
      let block: HTMLElement | null = mark.parentElement;
      while (block && block.parentElement !== editor.view.dom) {
        block = block.parentElement;
      }
      if (!block) continue;
      next[thread.id] = block.getBoundingClientRect().top - wrapRect.top;
    }
    if (JSON.stringify(next) !== JSON.stringify(threadTops)) setThreadTops(next);
    // A resolved thread keeps its anchor so it can be reopened, but loses the
    // yellow: the mark stays in the document, only the paint goes.
    const resolvedIds = new Set(
      comments.threads.filter((thread) => thread.resolved).map((thread) => thread.id),
    );
    for (const span of editor.view.dom.querySelectorAll('.cmt')) {
      const threadId = span.getAttribute('data-comment-id');
      span.classList.toggle('cmt--active', threadId === openThreadId);
      span.classList.toggle('cmt--resolved', threadId !== null && resolvedIds.has(threadId));
    }

    const rowCounts: Record<string, number> = {};
    editor.state.doc.forEach((node) => {
      const row = typeof node.attrs['row'] === 'string' ? node.attrs['row'] : null;
      if (row) {
        rowCounts[row] = (rowCounts[row] ?? 0) + 1;
      }
    });

    const rowIndex: Record<string, number> = {};
    const handles: ColHandle[] = [];
    editor.state.doc.forEach((node, offset) => {
      const dom = editor.view.nodeDOM(offset);
      if (!(dom instanceof HTMLElement)) return;
      const row = typeof node.attrs['row'] === 'string' ? node.attrs['row'] : null;
      const groupCount = row ? (rowCounts[row] ?? 1) : 1;
      const indexInRow = row ? (rowIndex[row] ?? 0) : 0;
      if (row) rowIndex[row] = indexInRow + 1;

      if (groupCount > 1 && indexInRow < groupCount - 1) {
        const rect = dom.getBoundingClientRect();
        handles.push({
          pos: offset,
          top: rect.top - wrapRect.top,
          left: rect.right - wrapRect.left,
          height: rect.height,
          hovered: gutter?.pos === offset || colDraggingPos === offset,
        });
      }
    });
    if (JSON.stringify(handles) !== JSON.stringify(colHandles)) setColHandles(handles);
  });

  useEffect(() => {
    if (selectedPositions.length === 0 && blockMenu === null) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (event.key === 'Escape') {
        event.preventDefault();
        setBlockMenu(null);
        setBlockMenuQuery('');
        setSelectedPositions([]);
        setGripSelectedPos(null);
        setMselTurnOpen(false);
        return;
      }
      if (target?.closest('input, textarea')) return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        deleteSelectedBlocks();
        setBlockMenu(null);
        setBlockMenuQuery('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    const onGlobalMouseDown = (event: MouseEvent): void => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;
      if (!target.closest('.slash')) {
        setQuery(null);
      }
      if (!target.closest('.selbar, .insertbar')) {
        setSelSub(null);
      }
      if (!target.closest('.cmt-compose, .selbar, .insertbar')) {
        setCompose(null);
      }
      if (
        !target.closest('.cmt-col, .cmt-page, .cmt-compose, .cmt-badge, [data-comment-id], .pghead')
      ) {
        setOpenThreadId(null);
        if (!pageDraft.trim()) {
          comments.onClosePageCompose();
        }
      }
    };
    window.addEventListener('mousedown', onGlobalMouseDown);
    return () => window.removeEventListener('mousedown', onGlobalMouseDown);
  });

  useEffect(() => {
    const docCanvas =
      wrapRef.current?.closest('.document') ?? wrapRef.current?.closest('.page') ?? null;
    if (!(docCanvas instanceof HTMLElement)) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (event.button !== 0 || !editor || !wrapRef.current) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;
      if (
        target.closest(
          'input, textarea, button:not(.editor-end), select, a, [role=menu], [role=listbox], [role=dialog], [role=toolbar], .blk-gutter, .col-handle, .cmt-col, .cmt-page, .cmt-compose, .pghead',
        )
      ) {
        return;
      }
      const wrapRect0 = wrapRef.current.getBoundingClientRect();
      const inContainerX = event.clientX >= wrapRect0.left && event.clientX <= wrapRect0.right;
      const inEditorBody = target.closest('.page-editor__body') !== null || inContainerX;

      if (inEditorBody) {
        if (selectedPositions.length > 0) {
          setSelectedPositions([]);
          setGripSelectedPos(null);
          setMselTurnOpen(false);
        }
        const blockElementsNow = Array.from(
          editor.view.dom.querySelectorAll<HTMLElement>('[data-blk-pos]'),
        );
        const lastBlockRect = blockElementsNow.at(-1)?.getBoundingClientRect() ?? null;
        if (
          target.closest('[data-blk-pos]') === null &&
          lastBlockRect !== null &&
          event.clientY > lastBlockRect.bottom
        ) {
          event.preventDefault();
          const last = editor.state.doc.lastChild;
          if (last && last.type.name === 'paragraph' && last.content.size === 0) {
            editor.chain().focus('end').run();
          } else {
            editor.chain().focus('end').insertContent({ type: 'paragraph' }).run();
          }
        }
        return;
      }

      event.preventDefault();
      const x0 = event.clientX;
      const y0 = event.clientY;
      let moved = false;

      const blockElements = (): HTMLElement[] =>
        Array.from(editor.view.dom.querySelectorAll<HTMLElement>('[data-blk-pos]'));

      const onMove = (moveEvent: MouseEvent): void => {
        if ((moveEvent.buttons & 1) === 0) {
          onUp();
          return;
        }
        if (!moved && Math.abs(moveEvent.clientX - x0) + Math.abs(moveEvent.clientY - y0) < 5) {
          return;
        }
        if (!moved) {
          moved = true;
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
          window.getSelection()?.removeAllRanges();
          document.body.style.userSelect = 'none';
          setBlockMenu(null);
          setMselTurnOpen(false);
          setGripSelectedPos(null);
        }
        const wrap = wrapRef.current;
        if (!wrap) return;
        const wrapRect = wrap.getBoundingClientRect();
        const left = Math.min(x0, moveEvent.clientX);
        const top = Math.min(y0, moveEvent.clientY);
        const right = Math.max(x0, moveEvent.clientX);
        const bottom = Math.max(y0, moveEvent.clientY);
        const positions = blockElements()
          .filter((el) => {
            const rect = el.getBoundingClientRect();
            return rect.right > left && rect.left < right && rect.bottom > top && rect.top < bottom;
          })
          .map((el) => Number(el.getAttribute('data-blk-pos')))
          .filter((pos) => !Number.isNaN(pos));
        setMarquee({
          left: left - wrapRect.left,
          top: top - wrapRect.top,
          width: right - left,
          height: bottom - top,
        });
        setSelectedPositions(positions);
      };

      const onUp = (): void => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('blur', onUp);
        document.body.style.userSelect = '';
        if (moved) {
          setMarquee(null);
        } else {
          setSelectedPositions([]);
          setGripSelectedPos(null);
          setMselTurnOpen(false);
        }
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('blur', onUp);
    };
    docCanvas.addEventListener('mousedown', onMouseDown);
    return () => docCanvas.removeEventListener('mousedown', onMouseDown);
  }, [editor, selectedPositions.length]);

  function startCompose(): void {
    if (!editor || !wrapRef.current) return;
    const { from, to, empty } = editor.state.selection;
    if (empty) return;
    const quote = editor.state.doc.textBetween(from, to, '\n');
    const coords = editor.view.coordsAtPos(from);
    const wrapRect = wrapRef.current.getBoundingClientRect();
    setCompose({
      quote,
      from,
      to,
      top: coords.bottom - wrapRect.top + 6,
      left: Math.max(0, Math.min(coords.left - wrapRect.left, wrapRect.width - 324)),
    });
    setComposeDraft('');
    setComposeFailed(false);
  }

  async function postCompose(): Promise<void> {
    if (!editor || !compose || !composeDraft.trim()) return;
    try {
      const thread = await comments.onCreateThread(compose.quote, composeDraft.trim());
      editor
        .chain()
        .setTextSelection({ from: compose.from, to: compose.to })
        .setMark('comment', { threadId: thread.id })
        .run();
      setCompose(null);
      setOpenThreadId(thread.id);
    } catch {
      setComposeFailed(true);
    }
  }

  async function postPageComment(): Promise<void> {
    if (!pageDraft.trim()) return;
    try {
      const thread = await comments.onCreateThread(null, pageDraft.trim());
      setPageDraft('');
      comments.onClosePageCompose();
      setOpenThreadId(thread.id);
    } catch {
      setComposeFailed(true);
    }
  }

  const threadActions = {
    onReply: comments.onReply,
    onEdit: comments.onEdit,
    onDelete: comments.onDelete,
    onSetResolved: async (threadId: string, resolved: boolean): Promise<void> => {
      await comments.onSetResolved(threadId, resolved);
      if (resolved) setOpenThreadId(null);
    },
    onToggle: setOpenThreadId,
  };

  function gutterFromPoint(
    clientX: number,
    clientY: number,
    target: EventTarget | null,
  ): GutterState | null {
    try {
      return gutterFromPointInner(clientX, clientY, target);
    } catch {
      // The editor view is gone during unmount; there is nothing to show.
      return null;
    }
  }

  function blockAtPoint(
    clientX: number,
    clientY: number,
    target?: EventTarget | null,
  ): { pos: number; element: HTMLElement; kind: BlockId } | null {
    const current = editor;
    if (!current) return null;
    let element: HTMLElement | null = target instanceof HTMLElement ? target : null;
    while (element && element.parentElement !== current.view.dom) {
      element = element.parentElement;
    }
    let pos: number | null = null;
    if (element && element.parentElement === current.view.dom) {
      const attr = element.getAttribute('data-blk-pos');
      if (attr !== null) {
        pos = Number(attr);
      } else {
        const inside = current.view.posAtDOM(element, 0);
        const $inside = current.state.doc.resolve(inside);
        pos = $inside.depth >= 1 ? $inside.before(1) : inside;
      }
    } else {
      const blocks = Array.from(current.view.dom.querySelectorAll<HTMLElement>('[data-blk-pos]'));
      for (const candidate of blocks) {
        const rect = candidate.getBoundingClientRect();
        if (
          clientY >= rect.top - 4 &&
          clientY <= rect.bottom + 4 &&
          clientX >= rect.left - COLUMN_GAP &&
          clientX <= rect.right + 24
        ) {
          element = candidate;
          pos = Number(candidate.getAttribute('data-blk-pos'));
          break;
        }
      }
      if (pos === null) {
        for (const candidate of blocks) {
          const rect = candidate.getBoundingClientRect();
          if (clientY >= rect.top - 4 && clientY <= rect.bottom + 4) {
            element = candidate;
            pos = Number(candidate.getAttribute('data-blk-pos'));
            break;
          }
        }
      }
    }
    if (pos === null || Number.isNaN(pos) || !element) return null;
    const node = current.state.doc.nodeAt(pos);
    if (!node) return null;
    const kind = kindOf(node.toJSON() as JSONContent);
    return { pos, element, kind };
  }

  function gutterFromPointInner(
    clientX: number,
    clientY: number,
    target: EventTarget | null,
  ): GutterState | null {
    const wrap = wrapRef.current;
    if (!wrap) return null;
    const found = blockAtPoint(clientX, clientY, target);
    if (!found) return null;
    const wrapRect = wrap.getBoundingClientRect();
    const elemRect = found.element.getBoundingClientRect();
    const top = elemRect.top - wrapRect.top + (GUTTER_OFFSET[found.kind] ?? 1);
    const left = elemRect.left - wrapRect.left - COLUMN_GAP;
    return { pos: found.pos, top, left, kind: found.kind };
  }

  function computeDropIndicator(
    clientX: number,
    clientY: number,
    target?: EventTarget | null,
  ): DropIndicator | null {
    const current = editor;
    const wrap = wrapRef.current;
    const fromPos = dragPosRef.current;
    if (!current || !wrap || fromPos === null) return null;
    const found = blockAtPoint(clientX, clientY, target);
    if (!found || found.pos === fromPos) return null;
    const draggingPositions = dragPositionsRef.current;
    if (draggingPositions && draggingPositions.includes(found.pos)) return null;

    const wrapRect = wrap.getBoundingClientRect();
    const rect = found.element.getBoundingClientRect();
    const px = clientX - rect.left;
    const node = current.state.doc.nodeAt(found.pos);
    const textLen = node?.textContent.length ?? 0;
    const charWidth = found.kind === 'h1' ? 15 : found.kind === 'h2' ? 12 : 8.5;
    const estimatedTextWidth =
      found.kind === 'divider' || found.kind === 'code' || found.kind === 'callout'
        ? 0
        : textLen * charWidth + 40;

    let side: 'left' | 'right' | null =
      px < 36
        ? 'left'
        : px > rect.width - 48 || px > Math.max(80, estimatedTextWidth + 64)
          ? 'right'
          : null;
    if (isMobile || (draggingPositions && draggingPositions.length > 1)) side = null;
    const after = isMobile && clientY > rect.top + rect.height / 2;

    return {
      pos: found.pos,
      side,
      after,
      top: (after ? rect.bottom : rect.top) - wrapRect.top,
      left: rect.left - wrapRect.left,
      width: rect.width,
      height: rect.height,
    };
  }

  function commitTopBlocks(nodes: JSONContent[]): void {
    const current = editor;
    if (!current) return;
    const tidied = tidyBlockRows(nodes);
    const nextNodes = tidied.length > 0 ? tidied : [{ type: 'paragraph' }];
    current.commands.setContent({ type: 'doc', content: nextNodes }, { emitUpdate: true });
  }

  function topBlockList(): Array<{ pos: number; json: JSONContent }> {
    const current = editor;
    if (!current) return [];
    const list: Array<{ pos: number; json: JSONContent }> = [];
    current.state.doc.forEach((node, offset) => {
      list.push({ pos: offset, json: node.toJSON() as JSONContent });
    });
    return list;
  }

  function dropBeside(fromPos: number, targetPos: number, side: 'left' | 'right'): void {
    const list = topBlockList();
    const fromEntry = list.find((entry) => entry.pos === fromPos);
    const targetEntry = list.find((entry) => entry.pos === targetPos);
    if (!fromEntry || !targetEntry) return;
    const targetRow =
      typeof targetEntry.json.attrs?.['row'] === 'string' ? targetEntry.json.attrs['row'] : null;
    const fromRow =
      typeof fromEntry.json.attrs?.['row'] === 'string' ? fromEntry.json.attrs['row'] : null;
    const rowId =
      targetRow ?? `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const memberCount = targetRow
      ? list.filter((entry) => entry.json.attrs?.['row'] === targetRow).length
      : 1;
    if (!(fromRow && fromRow === targetRow) && memberCount >= 4) {
      toast(t('editor.maxColumns'));
      return;
    }
    const rest = list
      .filter((entry) => entry.pos !== fromPos)
      .map((entry) =>
        entry.pos === targetPos
          ? {
              ...entry,
              json: {
                ...entry.json,
                attrs: {
                  ...entry.json.attrs,
                  row: rowId,
                  cw: typeof entry.json.attrs?.['cw'] === 'number' ? entry.json.attrs['cw'] : 1,
                },
              },
            }
          : entry,
      );
    const targetIndex = rest.findIndex((entry) => entry.pos === targetPos);
    if (targetIndex < 0) return;
    const movedJson: JSONContent = {
      ...fromEntry.json,
      attrs: {
        ...fromEntry.json.attrs,
        row: rowId,
        cw: typeof fromEntry.json.attrs?.['cw'] === 'number' ? fromEntry.json.attrs['cw'] : 1,
      },
    };
    rest.splice(side === 'left' ? targetIndex : targetIndex + 1, 0, {
      pos: fromPos,
      json: movedJson,
    });
    commitTopBlocks(rest.map((entry) => entry.json));
  }

  function moveBlocksVertically(movingPositions: number[], targetPos: number, after = false): void {
    const list = topBlockList();
    const movingSet = new Set(movingPositions);
    if (movingSet.has(targetPos)) return;
    const moved = list
      .filter((entry) => movingSet.has(entry.pos))
      .map((entry) => ({
        ...entry.json,
        attrs: { ...entry.json.attrs, row: null, cw: null },
      }));
    if (moved.length === 0) return;
    const rest = list.filter((entry) => !movingSet.has(entry.pos));
    let targetIndex = rest.findIndex((entry) => entry.pos === targetPos);
    if (targetIndex < 0) return;
    const targetRow =
      typeof rest[targetIndex]?.json.attrs?.['row'] === 'string'
        ? rest[targetIndex]?.json.attrs?.['row']
        : null;
    if (targetRow) {
      targetIndex = after
        ? rest.findLastIndex((entry) => entry.json.attrs?.['row'] === targetRow)
        : rest.findIndex((entry) => entry.json.attrs?.['row'] === targetRow);
    }
    const out = rest.map((entry) => entry.json);
    out.splice(targetIndex + (after ? 1 : 0), 0, ...moved);
    commitTopBlocks(out);
  }

  function applyDrop(indicator: DropIndicator | null): void {
    const fromPos = dragPosRef.current;
    const draggingPositions = dragPositionsRef.current;
    dragPosRef.current = null;
    dragPositionsRef.current = null;
    setDropIndicator(null);
    dropIndicatorRef.current = null;
    if (fromPos === null || !indicator || indicator.pos === fromPos) return;
    if (indicator.side && !(draggingPositions && draggingPositions.length > 1)) {
      dropBeside(fromPos, indicator.pos, indicator.side);
      setSelectedPositions([]);
      setGripSelectedPos(null);
      return;
    }
    const positionsToMove =
      draggingPositions && draggingPositions.length > 1 ? draggingPositions : [fromPos];
    moveBlocksVertically(positionsToMove, indicator.pos, indicator.after);
    setSelectedPositions([]);
    setGripSelectedPos(null);
  }

  function startColResize(event: React.PointerEvent, leftPos: number): void {
    event.preventDefault();
    event.stopPropagation();
    const current = editor;
    if (!current) return;
    const list = topBlockList();
    const leftEntry = list.find((entry) => entry.pos === leftPos);
    const rowId =
      typeof leftEntry?.json.attrs?.['row'] === 'string' ? leftEntry.json.attrs['row'] : null;
    if (!leftEntry || !rowId) return;
    const rowMembers = list.filter((entry) => entry.json.attrs?.['row'] === rowId);
    const indexInRow = rowMembers.findIndex((entry) => entry.pos === leftPos);
    const rightEntry = rowMembers[indexInRow + 1];
    if (!rightEntry) return;
    const leftDom = current.view.nodeDOM(leftPos);
    const rightDom = current.view.nodeDOM(rightEntry.pos);
    if (!(leftDom instanceof HTMLElement) || !(rightDom instanceof HTMLElement)) return;
    const r1 = leftDom.getBoundingClientRect();
    const r2 = rightDom.getBoundingClientRect();
    const startLeft = r1.left;
    const span = r2.right - r1.left - COLUMN_GAP;
    if (span <= 0) return;
    const cw1 = typeof leftEntry.json.attrs?.['cw'] === 'number' ? leftEntry.json.attrs['cw'] : 1;
    const cw2 = typeof rightEntry.json.attrs?.['cw'] === 'number' ? rightEntry.json.attrs['cw'] : 1;
    const pairSum = cw1 + cw2;

    setColDraggingPos(leftPos);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMove = (moveEvent: PointerEvent): void => {
      if (moveEvent.pointerId !== event.pointerId) return;
      const rawFraction = Math.max(0.15, Math.min(0.85, (moveEvent.clientX - startLeft) / span));
      const fraction = snapColumnFraction(rawFraction);
      const nextCw1 = Number((pairSum * fraction).toFixed(4));
      const nextCw2 = Number((pairSum - nextCw1).toFixed(4));
      current
        .chain()
        .command(({ tr, state }) => {
          const leftNode = state.doc.nodeAt(leftPos);
          const rightNode = state.doc.nodeAt(rightEntry.pos);
          if (!leftNode || !rightNode) return false;
          tr.setNodeMarkup(leftPos, undefined, { ...leftNode.attrs, cw: nextCw1 });
          tr.setNodeMarkup(rightEntry.pos, undefined, { ...rightNode.attrs, cw: nextCw2 });
          return true;
        })
        .run();
    };

    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('blur', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      setColDraggingPos(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('blur', onUp);
  }

  function startGripPointerDrag(event: React.PointerEvent, target: GutterState): void {
    if (event.button !== 0 || event.shiftKey || event.metaKey || event.ctrlKey) return;
    if (event.pointerType === 'mouse') event.preventDefault();
    didGripDragRef.current = false;
    stopDragRef.current?.();
    const clear = (): void => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      dragPosRef.current = null;
      dragPositionsRef.current = null;
      dropIndicatorRef.current = null;
      setDropIndicator(null);
      stopDragRef.current = null;
    };
    stopDragRef.current = trackBlockDrag(event, {
      begin: () => {
        didGripDragRef.current = true;
        dragPosRef.current = target.pos;
        const order = topBlockList().map((entry) => entry.pos);
        dragPositionsRef.current =
          selectedPositions.includes(target.pos) && selectedPositions.length > 1
            ? order.filter((pos) => selectedPositions.includes(pos))
            : null;
        setBlockMenu(null);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      },
      move: (moveEvent) => {
        const next = computeDropIndicator(
          moveEvent.clientX,
          moveEvent.clientY,
          document.elementFromPoint(moveEvent.clientX, moveEvent.clientY),
        );
        dropIndicatorRef.current = next;
        setDropIndicator(next);
      },
      drop: () => {
        applyDrop(dropIndicatorRef.current);
        setMobileMoving(false);
        clear();
      },
      cancel: clear,
    });
  }

  function deleteSelectedBlocks(): void {
    const list = topBlockList();
    if (selectedPositions.length === 0) return;
    const selectedSet = new Set(selectedPositions);
    const remaining = list
      .filter((entry) => !selectedSet.has(entry.pos))
      .map((entry) => entry.json);
    const count = selectedPositions.length;
    commitTopBlocks(remaining);
    setSelectedPositions([]);
    setGripSelectedPos(null);
    setMselTurnOpen(false);
    setBlockMenu(null);
    toast(count > 1 ? t('editor.blocksDeleted') : t('editor.blockDeleted'));
  }

  function duplicateSelectedBlocks(): void {
    const list = topBlockList();
    if (selectedPositions.length === 0) return;
    const selectedSet = new Set(selectedPositions);
    const out: JSONContent[] = [];
    for (const entry of list) {
      out.push(entry.json);
      if (selectedSet.has(entry.pos)) {
        const clone = JSON.parse(JSON.stringify(entry.json)) as JSONContent;
        out.push({
          ...clone,
          attrs: { ...clone.attrs, row: null, cw: null },
        });
      }
    }
    commitTopBlocks(out);
    setSelectedPositions([]);
    setGripSelectedPos(null);
    setMselTurnOpen(false);
  }

  function turnSelectedBlocksInto(targetId: BlockId): void {
    const list = topBlockList();
    if (selectedPositions.length === 0) return;
    const selectedSet = new Set(selectedPositions);
    const out = list.map((entry) =>
      selectedSet.has(entry.pos) ? convertBlockJson(entry.json, targetId) : entry.json,
    );
    commitTopBlocks(out);
    setSelectedPositions([]);
    setGripSelectedPos(null);
    setMselTurnOpen(false);
  }

  function addBelow(pos: number): void {
    const current = editor;
    if (!current) return;
    const size = current.state.doc.nodeAt(pos)?.nodeSize ?? 0;
    current
      .chain()
      .focus()
      .insertContentAt(pos + size, { type: 'paragraph', content: [{ type: 'text', text: '/' }] })
      .setTextSelection(pos + size + 2)
      .run();
  }

  /** Create a child page and put a reference to it where the slash line was. */
  const createChildPage = (current: Editor): void => {
    const top = blockAtSelection(current);
    if (!top) return;
    const size = current.state.doc.nodeAt(top.pos)?.nodeSize ?? 0;
    void createPage(workspaceId, { title: '', parentId: pageId })
      .then((created) => {
        current
          .chain()
          .focus()
          .insertContentAt(
            { from: top.pos, to: top.pos + size },
            {
              type: 'childPage',
              attrs: { pageId: created.id, title: created.title, icon: created.icon },
            },
          )
          .run();
      })
      .catch(() => toast(t('error.generic')));
  };

  /** Link the selected text to a page in this workspace. */
  function applyPageLink(target: PageSummary): void {
    if (!editor) return;
    // The range is kept first: unsetLink would otherwise collapse it.
    const { from, to } = editor.state.selection;
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .unsetLink()
      .setMark('pageLink', { pageId: target.id, href: `#p/${target.id}` })
      .run();
    setSelSub(null);
    setPageHits([]);
  }

  /** Look for pages while the link field is being filled in. */
  function searchForPages(query: string): void {
    const needle = query.trim();
    if (needle.length < 2) {
      setPageHits([]);
      return;
    }
    void searchPages(workspaceId, needle)
      .then(setPageHits)
      .catch(() => setPageHits([]));
  }

  function applyLink(): void {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const href = /^[a-z]+:/i.test(url) ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setSelSub(null);
  }

  const statusText =
    status === 'saving'
      ? t('page.saving')
      : status === 'saved'
        ? t('page.saved')
        : status === 'error'
          ? t('page.saveFailed')
          : status === 'conflict'
            ? t('page.conflict')
            : '';

  const activeKind = editor ? (blockAtSelection(editor)?.kind ?? 'text') : 'text';
  const activeDef = BLOCKS.find((block) => block.id === activeKind) ?? BLOCKS[0];

  const validSelectedPositions = useMemo(() => {
    if (!editor || selectedPositions.length === 0) return [];
    const available = new Set<number>();
    editor.state.doc.forEach((_node, offset) => available.add(offset));
    return selectedPositions.filter((pos) => available.has(pos));
  }, [editor, selectedPositions]);

  const selectedWords = useMemo(() => {
    if (!editor || validSelectedPositions.length === 0) return 0;
    let total = 0;
    for (const pos of validSelectedPositions) {
      const node = editor.state.doc.nodeAt(pos);
      const text = node?.textContent.trim() ?? '';
      if (text) total += text.split(/\s+/).length;
    }
    return total;
  }, [editor, validSelectedPositions]);

  const showMselBar =
    validSelectedPositions.length > 0 &&
    !(validSelectedPositions.length === 1 && gripSelectedPos === validSelectedPositions[0]);

  const startInsert = (): void => {
    if (!editor) return;
    const { $from } = editor.state.selection;
    const chain = editor.chain().focus();
    if ($from.parent.content.size > 0) chain.splitBlock();
    chain.insertContent('/').run();
  };

  const commentComposer = compose ? (
    <div
      className="cmt-compose"
      style={{ top: compose.top, left: compose.left }}
      role="dialog"
      aria-label={t('comments.writeComment')}
    >
      <p className="cmt-compose__quote">{compose.quote}</p>
      <textarea
        className="cmt-input cmt-input--area"
        autoFocus
        value={composeDraft}
        placeholder={t('comments.writeComment')}
        aria-label={t('comments.writeComment')}
        onChange={(event) => setComposeDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void postCompose();
          }
          if (event.key === 'Escape') setCompose(null);
        }}
      />
      <MentionList
        draft={composeDraft}
        members={comments.members}
        onPick={(text) => setComposeDraft(text)}
      />
      {composeFailed ? (
        <p className="cmt-card__error" role="alert">
          {t('comments.failed')}
        </p>
      ) : null}
      <div className="cmt-reply">
        <span className="cmt-reply__spacer" />
        <button type="button" className="cmt-resolveBtn nb" onClick={() => setCompose(null)}>
          {t('comments.cancel')}
        </button>
        <button
          type="button"
          className="cmt-btn cmt-btn--primary nb"
          disabled={!composeDraft.trim()}
          onClick={() => void postCompose()}
        >
          {t('comments.post')}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div
      className={mobileMoving ? 'page-editor page-editor--moving' : 'page-editor'}
      onContextMenu={(event) => {
        if (isMobile && mobileMoving) event.preventDefault();
      }}
      onPointerDownCapture={(event) => {
        if (
          !isMobile ||
          !mobileMoving ||
          event.pointerType !== 'touch' ||
          dragPosRef.current !== null
        )
          return;
        if (!(event.target instanceof Element) || !event.target.closest('.page-editor__body'))
          return;
        const found = blockAtPoint(event.clientX, event.clientY, event.target);
        if (!found) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        startGripPointerDrag(event, { pos: found.pos, kind: found.kind, top: 0, left: 0 });
      }}
      ref={wrapRef}
      onClick={(event) => {
        const mark = (event.target as HTMLElement).closest('[data-comment-id]');
        const threadId = mark?.getAttribute('data-comment-id');
        if (threadId) {
          comments.onShowComments();
          setOpenThreadId((current) => (current === threadId ? null : threadId));
        }
      }}
      onMouseMove={(event) => {
        if (blockMenu || dragPosRef.current !== null) return;
        const found = gutterFromPoint(event.clientX, event.clientY, event.target);
        if (found) setGutter(found);
      }}
      onMouseLeave={() => {
        if (!blockMenu) setGutter(null);
      }}
    >
      {editor && !isMobile ? (
        <BubbleMenu
          ref={selbarRef}
          editor={editor}
          className="selbar"
          options={{ placement: 'top', offset: 8 }}
          shouldShow={({ state }) =>
            !state.selection.empty &&
            query === null &&
            compose === null &&
            !(
              state.selection instanceof NodeSelection &&
              state.selection.node.type.name === 'tableOfContents'
            )
          }
        >
          <div className="selbar__row" role="toolbar" aria-label={t('editor.formatting')}>
            <TurnIntoMenu
              items={TURN_BLOCKS.map((block) => ({
                id: block.id,
                label: t(block.label),
                icon: <BlockIcon id={block.id} />,
                current: block.id === activeKind,
              }))}
              open={selSub === 'turn'}
              onOpenChange={(next) =>
                setSelSub((current) => (next ? 'turn' : current === 'turn' ? null : current))
              }
              onTurnInto={(id) => {
                const block = TURN_BLOCKS.find((candidate) => candidate.id === id);
                if (block) replaceBlock(editor, block.id, true);
              }}
              label={t('editor.turnInto')}
              side="bottom"
              portalContainer={selbarRef.current}
              trigger={
                <button type="button" className="selbar__turn nb" title={t('editor.turnInto')}>
                  <span>{activeDef ? t(activeDef.label) : ''}</span>
                  <Glyph d="m6 9 6 6 6-6" size={12} />
                </button>
              }
            />
            <span className="selbar__sep" aria-hidden="true" />
            <EditorFormatControls editor={editor} />
            <span className="selbar__sep" aria-hidden="true" />
            <button
              type="button"
              className={selSub === 'link' ? 'selbar__btn nb is-on' : 'selbar__btn nb'}
              aria-label={t('editor.link')}
              aria-pressed={editor.isActive('link')}
              title={t('editor.link')}
              onClick={() => setSelSub(selSub === 'link' ? null : 'link')}
            >
              <Glyph d={ICON.link} size={16} />
            </button>
            <span className="selbar__sep" aria-hidden="true" />
            <button
              type="button"
              className="selbar__cmt nb"
              title={t('editor.comment')}
              onMouseDown={(event) => {
                event.preventDefault();
                startCompose();
              }}
            >
              <Glyph d={ICON.comment} size={16} />
              <span>{t('editor.comment')}</span>
            </button>
          </div>
          {selSub === 'link' ? (
            <div className="selbar__sub selbar__link">
              <input
                value={linkUrl}
                placeholder={t('editor.linkPlaceholder')}
                aria-label={t('editor.link')}
                autoFocus
                onChange={(event) => {
                  setLinkUrl(event.target.value);
                  searchForPages(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyLink();
                  }
                }}
              />
              <button type="button" className="btn-prim nb" onClick={applyLink}>
                {t('editor.linkApply')}
              </button>
              {pageHits.length > 0 ? (
                <ul className="selbar__pages">
                  {pageHits.map((hit) => (
                    <li key={hit.id}>
                      <button
                        type="button"
                        className="nb selbar__page"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          applyPageLink(hit);
                        }}
                      >
                        <span className="selbar__pageicon" aria-hidden="true">
                          {hit.icon || '📄'}
                        </span>
                        {hit.title}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </BubbleMenu>
      ) : null}

      {isMobile &&
      editor &&
      (editorFocused || compose || mobileMoving) &&
      editor.isEditable &&
      !blockMenu ? (
        <InsertBar
          editor={editor}
          onInsert={startInsert}
          onComment={startCompose}
          moving={mobileMoving}
          onToggleMove={() => {
            stopDragRef.current?.();
            setMobileMoving((current) => !current);
            editor.commands.blur();
          }}
          turnItems={TURN_BLOCKS.map((block) => ({
            id: block.id,
            label: t(block.label),
            icon: <BlockIcon id={block.id} />,
            current: block.id === activeKind,
          }))}
          onTurnInto={(id) => {
            const block = TURN_BLOCKS.find((candidate) => candidate.id === id);
            if (block) replaceBlock(editor, block.id, true);
          }}
          commentComposer={commentComposer}
        />
      ) : null}

      {!comments.hidden && (pageThreads.length > 0 || comments.pageComposeOpen) ? (
        <div className="cmt-page">
          {comments.pageComposeOpen ? (
            <div className="cmt-pageform">
              <textarea
                className="cmt-input cmt-input--area"
                rows={2}
                autoFocus
                value={pageDraft}
                placeholder={t('comments.pagePlaceholder')}
                aria-label={t('comments.pagePlaceholder')}
                onChange={(event) => setPageDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void postPageComment();
                  }
                  if (event.key === 'Escape') {
                    setPageDraft('');
                    comments.onClosePageCompose();
                  }
                }}
              />
              <MentionList
                draft={pageDraft}
                members={comments.members}
                onPick={(text) => setPageDraft(text)}
              />
              {composeFailed ? (
                <p className="cmt-card__error" role="alert">
                  {t('comments.failed')}
                </p>
              ) : null}
              <div className="cmt-reply">
                <span className="cmt-reply__spacer" />
                <button
                  type="button"
                  className="cmt-resolveBtn nb"
                  onClick={() => {
                    setPageDraft('');
                    comments.onClosePageCompose();
                  }}
                >
                  {t('comments.cancel')}
                </button>
                <button
                  type="button"
                  className="cmt-btn cmt-btn--primary nb"
                  disabled={!pageDraft.trim()}
                  onClick={() => void postPageComment()}
                >
                  {t('comments.comment')}
                </button>
              </div>
            </div>
          ) : null}
          {pageThreads.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              open={openThreadId === thread.id}
              currentUserId={comments.currentUserId}
              members={comments.members}
              actions={threadActions}
            />
          ))}
        </div>
      ) : null}

      <EditorContent editor={editor} />

      {!comments.hidden
        ? anchoredThreads.map((thread) => (
            <div key={thread.id} className="cmt-col" style={{ top: threadTops[thread.id] }}>
              <ThreadCard
                thread={thread}
                open={openThreadId === thread.id}
                currentUserId={comments.currentUserId}
                members={comments.members}
                actions={threadActions}
              />
            </div>
          ))
        : Object.entries(
            anchoredThreads.reduce<Record<string, CommentThread[]>>((groups, thread) => {
              const top = threadTops[thread.id] ?? 0;
              (groups[top] ??= []).push(thread);
              return groups;
            }, {}),
          ).map(([top, group]) => (
            <button
              key={top}
              type="button"
              className="cmt-badge nb"
              style={{ top: Number(top) + 2 }}
              aria-label={t('comments.show', { count: String(group.length) })}
              onClick={() => {
                comments.onShowComments();
                setOpenThreadId(group[0]?.id ?? null);
              }}
            >
              <Glyph d={ICON.comment} size={13} />
              <span>{group.length}</span>
            </button>
          ))}

      {!isMobile ? commentComposer : null}

      {gutter || blockMenu ? (
        <div
          className="blk-gutter"
          style={{
            top: (blockMenu ?? gutter)?.top,
            left: (blockMenu ?? gutter)?.left,
          }}
          aria-hidden={blockMenu ? undefined : true}
        >
          <button
            type="button"
            className="blk-gutter__btn nb"
            tabIndex={-1}
            aria-label={t('editor.addBelow')}
            title={t('editor.addBelow')}
            onClick={() => {
              const target = blockMenu ?? gutter;
              if (target) addBelow(target.pos);
              setBlockMenu(null);
            }}
          >
            <Glyph d={ICON.plus} size={18} />
          </button>
          <button
            type="button"
            className="blk-gutter__btn nb grip"
            aria-label={t('editor.moveOrOptions')}
            aria-haspopup="menu"
            aria-expanded={blockMenu !== null}
            title={t('editor.moveOrOptions')}
            onPointerDown={(event) => {
              const target = blockMenu ?? gutter;
              if (target) startGripPointerDrag(event, target);
            }}
            onContextMenu={(event) => event.preventDefault()}
            onClick={(event) => {
              if (didGripDragRef.current) {
                didGripDragRef.current = false;
                return;
              }
              const target = blockMenu ?? gutter;
              if (!target) return;
              if (event.shiftKey || event.metaKey || event.ctrlKey) {
                setSelectedPositions((current) =>
                  current.includes(target.pos)
                    ? current.filter((pos) => pos !== target.pos)
                    : [...current, target.pos],
                );
                setGripSelectedPos(null);
                setBlockMenu(null);
                return;
              }
              if (selectedPositions.length > 1 && selectedPositions.includes(target.pos)) {
                return;
              }
              const opening = blockMenu === null || blockMenu.pos !== target.pos;
              setBlockMenu(opening ? target : null);
              setBlockMenuQuery('');
              setSelectedPositions(opening ? [target.pos] : []);
              setGripSelectedPos(opening ? target.pos : null);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="9" cy="6" r="1.4" />
              <circle cx="15" cy="6" r="1.4" />
              <circle cx="9" cy="12" r="1.4" />
              <circle cx="15" cy="12" r="1.4" />
              <circle cx="9" cy="18" r="1.4" />
              <circle cx="15" cy="18" r="1.4" />
            </svg>
          </button>
        </div>
      ) : null}

      {blockMenu && editor
        ? (() => {
            const closeMenu = (): void => {
              setBlockMenu(null);
              setBlockMenuQuery('');
              setSelectedPositions([]);
              setGripSelectedPos(null);
            };
            const q = blockMenuQuery.trim().toLowerCase();
            const hit = (...words: string[]): boolean =>
              !q || words.some((word) => word.toLowerCase().includes(q));
            const canTurn = blockMenu.kind !== 'divider' && blockMenu.kind !== 'toc';
            const showTurn =
              canTurn &&
              hit(
                t('editor.turnInto'),
                'text',
                'heading',
                'list',
                'quote',
                'callout',
                'code',
                'toggle',
                'to-do',
              );
            const showColor =
              canTurn && hit(t('editor.color'), 'colour', 'background', 'text color');
            const node = editor.state.doc.nodeAt(blockMenu.pos);
            const curColor = typeof node?.attrs?.['color'] === 'string' ? node.attrs['color'] : '';
            const curBg = typeof node?.attrs?.['bg'] === 'string' ? node.attrs['bg'] : '';

            const setBlockColorAttr = (attr: 'color' | 'bg', value: string): void => {
              editor
                .chain()
                .command(({ tr, state }) => {
                  const targetNode = state.doc.nodeAt(blockMenu.pos);
                  if (!targetNode) return false;
                  tr.setNodeMarkup(blockMenu.pos, undefined, {
                    ...targetNode.attrs,
                    [attr]: value || null,
                  });
                  return true;
                })
                .run();
              closeMenu();
            };

            const moveBlockBy = (delta: -1 | 1): void => {
              const list = topBlockList();
              const indexInList = list.findIndex((entry) => entry.pos === blockMenu.pos);
              const nextIndex = indexInList + delta;
              if (indexInList >= 0 && nextIndex >= 0 && nextIndex < list.length) {
                const reordered = list.map((entry) => entry.json);
                const currentItem = reordered[indexInList];
                const swapItem = reordered[nextIndex];
                if (currentItem && swapItem) {
                  reordered[indexInList] = swapItem;
                  reordered[nextIndex] = currentItem;
                  commitTopBlocks(reordered);
                }
              }
              closeMenu();
            };

            const turnBlockInto = (id: string): void => {
              const block = TURN_BLOCKS.find((candidate) => candidate.id === id);
              if (!block) return;
              const heading =
                block.id === 'h1' || block.id === 'h2' || block.id === 'h3' || block.id === 'h4';
              const at =
                heading && blockMenu.kind === 'callout'
                  ? calloutLinePos(editor, blockMenu.pos)
                  : blockMenu.pos;
              replaceBlock(editor, block.id, true, at);
              closeMenu();
            };

            const actions: Array<{
              key: string;
              label: string;
              glyph: string;
              hint: string;
              danger?: boolean;
              run: () => void;
            }> = [
              {
                key: 'comment',
                label: t('editor.comment'),
                glyph: '✎',
                hint: '⌘⇧M',
                run: () => {
                  closeMenu();
                  toast(t('editor.selectTextToComment'));
                },
              },
              {
                key: 'copyLink',
                label: t('editor.copyLinkToBlock'),
                glyph: '⧉',
                hint: '',
                run: () => {
                  const url = `${window.location.origin}${window.location.pathname}#block-${blockMenu.pos}`;
                  void navigator.clipboard?.writeText(url);
                  closeMenu();
                  toast(t('editor.linkCopied'));
                },
              },
              {
                key: 'moveTo',
                label: t('editor.moveTo'),
                glyph: '↪',
                hint: '⌘⇧P',
                run: () => {
                  closeMenu();
                  toast(t('editor.chooseTargetPage'));
                },
              },
              {
                key: 'duplicate',
                label: t('editor.duplicate'),
                glyph: '❐',
                hint: '⌘D',
                run: () => {
                  if (node) {
                    const clone = node.toJSON() as JSONContent;
                    editor
                      .chain()
                      .insertContentAt(blockMenu.pos + node.nodeSize, {
                        ...clone,
                        attrs: { ...clone.attrs, row: null, cw: null },
                      })
                      .run();
                  }
                  closeMenu();
                },
              },
              {
                key: 'moveUp',
                label: t('editor.moveUp'),
                glyph: '↑',
                hint: '',
                run: () => moveBlockBy(-1),
              },
              {
                key: 'moveDown',
                label: t('editor.moveDown'),
                glyph: '↓',
                hint: '',
                run: () => moveBlockBy(1),
              },
              {
                key: 'delete',
                label: t('editor.delete'),
                glyph: '✕',
                hint: 'Del',
                danger: true,
                run: () => {
                  const list = topBlockList();
                  const remaining = list
                    .filter((entry) => entry.pos !== blockMenu.pos)
                    .map((entry) => entry.json);
                  commitTopBlocks(remaining);
                  closeMenu();
                  toast(t('editor.blockDeleted'));
                },
              },
            ].filter((action) => hit(action.label));

            const currentMember = comments.members.find(
              (member) => member.id === comments.currentUserId,
            );
            const authorName = (currentMember?.name ?? 'Arko').split(' ')[0] ?? 'Arko';

            return (
              <BlockActionMenu
                open
                onOpenChange={(next) => {
                  if (!next) closeMenu();
                }}
                anchorStyle={{ top: blockMenu.top, left: blockMenu.left }}
                query={blockMenuQuery}
                onQueryChange={setBlockMenuQuery}
                onEmptyBackspace={() => {
                  deleteSelectedBlocks();
                  closeMenu();
                }}
                onCloseFocus={() => editor.commands.focus()}
                currentKindLabel={t(
                  BLOCKS.find((block) => block.id === blockMenu.kind)?.label ?? 'editor.text',
                )}
                turnItems={
                  showTurn
                    ? TURN_BLOCKS.map((block) => ({
                        id: block.id,
                        label: t(block.label),
                        icon: <BlockIcon id={block.id} />,
                        current: block.id === blockMenu.kind,
                      }))
                    : null
                }
                onTurn={turnBlockInto}
                textColors={
                  showColor
                    ? BLOCK_TEXT_COLORS.map((swatch) => ({
                        id: swatch.id,
                        label: t(swatch.label),
                        current: curColor === swatch.id,
                      }))
                    : null
                }
                backgroundColors={
                  showColor
                    ? BLOCK_BG_COLORS.map((swatch) => ({
                        id: swatch.id,
                        label: t(swatch.label),
                        current: curBg === swatch.id,
                      }))
                    : null
                }
                onTextColor={(id) => setBlockColorAttr('color', id)}
                onBackgroundColor={(id) => setBlockColorAttr('bg', id)}
                actions={actions}
                footer={q ? null : t('editor.lastEditedBy', { name: authorName })}
              />
            );
          })()
        : null}

      {colHandles.map((handle) => (
        <div
          key={handle.pos}
          className={handle.hovered ? 'col-handle is-visible' : 'col-handle'}
          role="separator"
          aria-orientation="vertical"
          aria-label={t('editor.resizeColumns')}
          title={t('editor.resizeColumns')}
          style={{ top: handle.top, left: handle.left, height: handle.height }}
          onPointerDown={(event) => startColResize(event, handle.pos)}
          onContextMenu={(event) => event.preventDefault()}
        >
          <i />
        </div>
      ))}

      {dropIndicator !== null ? (
        <div
          className={
            dropIndicator.side === 'left'
              ? 'blk-drop blk-drop--left'
              : dropIndicator.side === 'right'
                ? 'blk-drop blk-drop--right'
                : 'blk-drop'
          }
          style={
            dropIndicator.side === 'left'
              ? {
                  top: dropIndicator.top,
                  left: dropIndicator.left - 3,
                  height: dropIndicator.height,
                }
              : dropIndicator.side === 'right'
                ? {
                    top: dropIndicator.top,
                    left: dropIndicator.left + dropIndicator.width,
                    height: dropIndicator.height,
                  }
                : {
                    top: dropIndicator.top,
                    left: dropIndicator.left,
                    width: dropIndicator.width,
                  }
          }
          aria-hidden="true"
        />
      ) : null}

      {marquee !== null ? (
        <div
          className="mq-box"
          style={{
            left: marquee.left,
            top: marquee.top,
            width: marquee.width,
            height: marquee.height,
          }}
          aria-hidden="true"
        />
      ) : null}

      {showMselBar ? (
        <div className="msel-bar" role="toolbar" aria-label={t('editor.selectedBlocks')}>
          <span className="msel-bar__label">
            {t('editor.selCount', {
              blocks:
                validSelectedPositions.length === 1
                  ? t('editor.oneBlock')
                  : t('editor.manyBlocks', { count: String(validSelectedPositions.length) }),
              words:
                selectedWords === 1
                  ? t('editor.oneWord')
                  : t('editor.manyWords', { count: String(selectedWords) }),
            })}
          </span>
          <TurnIntoMenu
            items={TURN_BLOCKS.map((block) => ({
              id: block.id,
              label: t(block.label),
              icon: <BlockIcon id={block.id} />,
            }))}
            open={mselTurnOpen}
            onOpenChange={setMselTurnOpen}
            onTurnInto={(id) => {
              const block = TURN_BLOCKS.find((candidate) => candidate.id === id);
              if (block) turnSelectedBlocksInto(block.id);
            }}
            label={t('editor.turnSelectedInto')}
            side="top"
            trigger={
              <button type="button" className="msel-bar__btn nb row">
                {t('editor.turnIntoCaret')}
              </button>
            }
          />
          <button type="button" className="msel-bar__btn nb row" onClick={duplicateSelectedBlocks}>
            {t('editor.duplicate')}
          </button>
          <button
            type="button"
            className="msel-bar__btn msel-bar__btn--danger nb row"
            onClick={deleteSelectedBlocks}
          >
            {t('editor.delete')}
          </button>
          <button
            type="button"
            className="msel-bar__btn nb row"
            onClick={() => {
              setSelectedPositions([]);
              setGripSelectedPos(null);
              setMselTurnOpen(false);
            }}
          >
            {t('editor.clear')}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className="editor-end nb"
        aria-label={t('editor.addAtEnd')}
        onClick={(event) => {
          // Only trigger via keyboard activation; mouse clicks below the last block
          // are already handled in the `.document` mousedown/mouseup handler.
          if (event.detail !== 0 || !editor) return;
          const last = editor.state.doc.lastChild;
          if (last && last.type.name === 'paragraph' && last.content.size === 0) {
            editor.chain().focus('end').run();
          } else {
            editor.chain().focus('end').insertContent({ type: 'paragraph' }).run();
          }
        }}
      />

      <p
        className={status === 'conflict' || status === 'error' ? 'page-editor__status' : 'sr-only'}
        role="status"
      >
        {statusText}
        {status === 'conflict' ? (
          <>
            {' '}
            <button type="button" onClick={() => window.location.reload()}>
              {t('page.reload')}
            </button>
          </>
        ) : null}
      </p>

      <Backlinks pageId={pageId} />

      <input
        ref={mediaInputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) attachMedia(file);
        }}
      />

      {mention !== null ? (
        <MentionPicker
          members={mentionMembers}
          activeIndex={mentionRef.current.index}
          onActiveChange={setMentionIndex}
          onPick={(member) => {
            if (!editor) return;
            insertMention(editor, mentionRef.current.from, mentionRef.current.to, member);
            setMention(null);
          }}
          caret={mention.caret}
        />
      ) : null}

      {query !== null && slashCaret ? (
        <SlashMenu
          items={items.map((item) => ({
            id: item.id,
            glyph: item.glyph,
            label: t(item.label),
            desc: t(item.desc),
            group: item.group
              ? t(item.group === 'media' ? 'slash.group.media' : 'slash.group.basic')
              : undefined,
          }))}
          activeIndex={index}
          onActiveChange={setIndex}
          caret={slashCaret}
          mobile={isMobile}
          onPick={(id) => {
            const block = items.find((candidate) => candidate.id === id);
            if (!editor || !block) return;
            if (block.id === 'database') insertDatabase(editor);
            else if (block.id === 'childpage') createChildPage(editor);
            else if (MEDIA_BLOCKS.includes(block.id)) pickMedia(block.id);
            else replaceBlock(editor, block.id, false);
            setQuery(null);
          }}
        />
      ) : null}
    </div>
  );
}
